import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getEmployee, setAdExclusion, setContactExclusion, setContactDisplayName, setContactName, getEmployeeADDetail } from "../services/api";
import ProgressBar from "../components/ProgressBar";

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: "#999", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, color: "#222" }}>{value || "—"}</div>
    </div>
  );
}

export default function EmployeeDetailPage() {
  const { hrlabId } = useParams();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const [excludeReason, setExcludeReason] = useState("");
  const [excludeSaving, setExcludeSaving] = useState(false);
  const [excludeError, setExcludeError] = useState(null);

  const [contactExcludeReason, setContactExcludeReason] = useState("");
  const [contactExcludeSaving, setContactExcludeSaving] = useState(false);
  const [contactExcludeError, setContactExcludeError] = useState(null);

  const [displayNameValue, setDisplayNameValue] = useState("");
  const [displayNameSaving, setDisplayNameSaving] = useState(false);
  const [displayNameError, setDisplayNameError] = useState(null);

  const [contactFirstNameValue, setContactFirstNameValue] = useState("");
  const [contactFirstNameOmit, setContactFirstNameOmit] = useState(false);
  const [contactLastNameValue, setContactLastNameValue] = useState("");
  const [contactLastNameOmit, setContactLastNameOmit] = useState(false);
  const [contactNameSaving, setContactNameSaving] = useState(false);
  const [contactNameError, setContactNameError] = useState(null);

  const [adDetail, setAdDetail] = useState(null);
  const [adDetailLoading, setAdDetailLoading] = useState(false);
  const [adDetailError, setAdDetailError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getEmployee(hrlabId)
      .then((res) => {
        setEmp(res.data.employee);
        setExcludeReason(res.data.employee.ad_excluded_reason || "");
        setContactExcludeReason(res.data.employee.contact_excluded_reason || "");
        setDisplayNameValue(res.data.employee.contact_display_name || "");
        const cfn = res.data.employee.contact_first_name;
        setContactFirstNameOmit(cfn === "");
        setContactFirstNameValue(cfn === "" || cfn === null || cfn === undefined ? "" : cfn);
        const cln = res.data.employee.contact_last_name;
        setContactLastNameOmit(cln === "");
        setContactLastNameValue(cln === "" || cln === null || cln === undefined ? "" : cln);
        if (res.data.employee.ad_user_id) {
          setAdDetailLoading(true);
          getEmployeeADDetail(hrlabId)
            .then((r) => setAdDetail(r.data.detail))
            .catch((err) => setAdDetailError(err.response?.data?.error || err.message))
            .finally(() => setAdDetailLoading(false));
        }
      })
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [hrlabId]);

  const handleToggleExclude = async (excluded) => {
    setExcludeSaving(true);
    setExcludeError(null);
    try {
      const res = await setAdExclusion(hrlabId, { excluded, reason: excludeReason });
      setEmp(res.data.employee);
    } catch (err) {
      setExcludeError(err.response?.data?.error || err.message);
    } finally {
      setExcludeSaving(false);
    }
  };

  const handleToggleContactExclude = async (excluded) => {
    setContactExcludeSaving(true);
    setContactExcludeError(null);
    try {
      const res = await setContactExclusion(hrlabId, { excluded, reason: contactExcludeReason });
      setEmp(res.data.employee);
    } catch (err) {
      setContactExcludeError(err.response?.data?.error || err.message);
    } finally {
      setContactExcludeSaving(false);
    }
  };

  const handleSaveDisplayName = async () => {
    setDisplayNameSaving(true);
    setDisplayNameError(null);
    try {
      const res = await setContactDisplayName(hrlabId, displayNameValue);
      setEmp(res.data.employee);
      setDisplayNameValue(res.data.employee.contact_display_name || "");
    } catch (err) {
      setDisplayNameError(err.response?.data?.error || err.message);
    } finally {
      setDisplayNameSaving(false);
    }
  };

  // field: "first_name" | "last_name" - speichert den Vor-/Nachname-Override
  // für die Kontaktverteilung. omit=true -> Feld wird im Kontakt weggelassen
  // (""), sonst leerer Wert = Standard (null) bzw. Override-Text.
  const saveContactNameField = async (field, value, omit) => {
    setContactNameSaving(true);
    setContactNameError(null);
    try {
      const payloadValue = omit ? "" : ((value || "").trim() || null);
      const res = await setContactName(hrlabId, { [field]: payloadValue });
      setEmp(res.data.employee);
      const col = field === "first_name" ? "contact_first_name" : "contact_last_name";
      const newVal = res.data.employee[col];
      if (field === "first_name") {
        setContactFirstNameOmit(newVal === "");
        setContactFirstNameValue(newVal === "" || newVal === null ? "" : newVal);
      } else {
        setContactLastNameOmit(newVal === "");
        setContactLastNameValue(newVal === "" || newVal === null ? "" : newVal);
      }
    } catch (err) {
      setContactNameError(err.response?.data?.error || err.message);
    } finally {
      setContactNameSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, maxWidth: 400, margin: "0 auto" }}><ProgressBar label="Lade Mitarbeiterdaten…" /></div>;
  if (error) return <div style={{ padding: 40, textAlign: "center", color: "#d13438" }}>❌ {error}</div>;
  if (!emp) return null;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Link to="/employees" style={{ fontSize: 13, color: "#0078d4", textDecoration: "none" }}>← Zurück zur Liste</Link>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>
          {emp.first_name} {emp.last_name}
        </h1>
        <span className={`badge badge-${emp.status === "active" ? "success" : "info"}`}>
          {emp.status === "active" ? "Aktiv" : "Inaktiv"}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>

        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>Kontakt</h2>
          <Field label="Geschäftliche E-Mail" value={emp.ad_upn || (emp.ad_user_id ? null : "— noch kein AD-Konto")} />
          {emp.email && emp.email.toLowerCase() !== (emp.ad_upn || "").toLowerCase() && (
            <Field label="E-Mail (HRLab)" value={emp.email} />
          )}
          <Field label="Telefon" value={emp.phone} />
          <Field label="Mobil (Arbeit)" value={emp.mobile} />
        </div>

        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>Stelle</h2>
          <Field label="Abteilung" value={emp.department} />
          <Field label="Position" value={emp.position} />
          <Field label="Eintrittsdatum" value={emp.start_date ? new Date(emp.start_date).toLocaleDateString("de-DE") : null} />
          <Field label="Austrittsdatum" value={emp.end_date ? new Date(emp.end_date).toLocaleDateString("de-DE") : null} />
        </div>

        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>Azure AD</h2>
          <Field label="Status" value={
            emp.ad_user_id ? "✓ Vorhanden" : (emp.last_synced ? "✗ Nicht gefunden" : "— Noch nicht synchronisiert")
          } />
          <Field label="AD-Benutzer-ID" value={emp.ad_user_id} />
          <Field label="Konto aktiviert" value={emp.ad_account_enabled === null ? null : (emp.ad_account_enabled ? "Ja" : "Nein")} />
          <Field label="Deaktiviert am" value={emp.ad_deactivated_at ? new Date(emp.ad_deactivated_at).toLocaleString("de-DE") : null} />
          {emp.scheduled_delete && (
            <Field label="Geplante Löschung" value={`${new Date(emp.scheduled_delete).toLocaleDateString("de-DE")} (${emp.days_until_delete} Tage)`} />
          )}

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!emp.ad_excluded}
                disabled={excludeSaving}
                onChange={(e) => handleToggleExclude(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 2 }}
              />
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                Kein Azure-AD-Konto nötig
                <div style={{ fontSize: 12, fontWeight: 400, color: "#888", marginTop: 2 }}>
                  Mitarbeiter taucht nicht mehr als "fehlt in Azure AD" / in der Sync-Vorschau auf.
                </div>
              </span>
            </label>

            {emp.ad_excluded && (
              <div style={{ marginTop: 10, marginLeft: 28 }}>
                <input
                  type="text"
                  placeholder="Grund (optional, z.B. externe Kraft)"
                  value={excludeReason}
                  onChange={(e) => setExcludeReason(e.target.value)}
                  onBlur={() => handleToggleExclude(true)}
                  style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, outline: "none" }}
                />
                {emp.ad_excluded_at && (
                  <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                    Markiert am {new Date(emp.ad_excluded_at).toLocaleString("de-DE")}
                    {emp.ad_excluded_by ? ` von ${emp.ad_excluded_by}` : ""}
                  </div>
                )}
              </div>
            )}

            {excludeError && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#d13438" }}>❌ {excludeError}</div>
            )}
          </div>
        </div>

        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>Sync-Info</h2>
          <Field label="HRLab-ID" value={emp.hrlab_id} />
          <Field label="Zuletzt synchronisiert" value={emp.last_synced ? new Date(emp.last_synced).toLocaleString("de-DE") : null} />
        </div>

        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>Kontaktverteilung</h2>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Anzeigename für Kontakt</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder={`${emp.first_name} ${emp.last_name}`}
                value={displayNameValue}
                disabled={displayNameSaving}
                onChange={(e) => setDisplayNameValue(e.target.value)}
                onBlur={handleSaveDisplayName}
                style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, outline: "none" }}
              />
            </div>
            <div style={{ fontSize: 12, fontWeight: 400, color: "#888", marginTop: 4 }}>
              Wird im "Mitarbeiterverzeichnis" anderer Mitarbeiter angezeigt, z.B. um lange
              HRLab-Namen zu kürzen (Standard: "{emp.first_name} {emp.last_name}").
            </div>
            {displayNameError && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#d13438" }}>❌ {displayNameError}</div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Vor-/Nachname für Kontakt</div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder={emp.first_name || ""}
                  value={contactFirstNameValue}
                  disabled={contactNameSaving || contactFirstNameOmit}
                  onChange={(e) => setContactFirstNameValue(e.target.value)}
                  onBlur={() => saveContactNameField("first_name", contactFirstNameValue, contactFirstNameOmit)}
                  style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, outline: "none" }}
                />
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#888", marginTop: 4, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={contactFirstNameOmit}
                    disabled={contactNameSaving}
                    onChange={(e) => {
                      setContactFirstNameOmit(e.target.checked);
                      saveContactNameField("first_name", contactFirstNameValue, e.target.checked);
                    }}
                    style={{ width: 14, height: 14 }}
                  />
                  Vorname nicht mitgeben
                </label>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder={emp.last_name || ""}
                  value={contactLastNameValue}
                  disabled={contactNameSaving || contactLastNameOmit}
                  onChange={(e) => setContactLastNameValue(e.target.value)}
                  onBlur={() => saveContactNameField("last_name", contactLastNameValue, contactLastNameOmit)}
                  style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, outline: "none" }}
                />
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#888", marginTop: 4, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={contactLastNameOmit}
                    disabled={contactNameSaving}
                    onChange={(e) => {
                      setContactLastNameOmit(e.target.checked);
                      saveContactNameField("last_name", contactLastNameValue, e.target.checked);
                    }}
                    style={{ width: 14, height: 14 }}
                  />
                  Nachname nicht mitgeben
                </label>
              </div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 400, color: "#888", marginTop: 6 }}>
              Manche Geräte zeigen statt des Anzeigenamens den im Kontakt hinterlegten
              Vor-/Nachnamen an (Standard: "{emp.first_name}" / "{emp.last_name}").
              Hier abweichende Werte eintragen oder per Häkchen das jeweilige Feld
              im Kontakt ganz weglassen.
            </div>
            {contactNameError && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#d13438" }}>❌ {contactNameError}</div>
            )}
          </div>

          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!emp.contact_excluded}
              disabled={contactExcludeSaving}
              onChange={(e) => handleToggleContactExclude(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 2 }}
            />
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Von Kontaktverteilung ausschließen
              <div style={{ fontSize: 12, fontWeight: 400, color: "#888", marginTop: 2 }}>
                Konto bekommt kein eigenes "Mitarbeiterverzeichnis" und wird nicht als Kontakt
                bei anderen Mitarbeitern angelegt (z.B. Service-/Funktionskonten).
              </div>
            </span>
          </label>

          {emp.contact_excluded && (
            <div style={{ marginTop: 10, marginLeft: 28 }}>
              <input
                type="text"
                placeholder="Grund (optional, z.B. Service-Account)"
                value={contactExcludeReason}
                onChange={(e) => setContactExcludeReason(e.target.value)}
                onBlur={() => handleToggleContactExclude(true)}
                style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, outline: "none" }}
              />
              {emp.contact_excluded_at && (
                <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                  Markiert am {new Date(emp.contact_excluded_at).toLocaleString("de-DE")}
                  {emp.contact_excluded_by ? ` von ${emp.contact_excluded_by}` : ""}
                </div>
              )}
            </div>
          )}

          {contactExcludeError && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#d13438" }}>❌ {contactExcludeError}</div>
          )}
        </div>

        {emp.ad_user_id && (
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>AD-Profil &amp; Lizenzen</h2>

            {adDetailLoading && <ProgressBar label="Lade AD-Daten…" />}

            {adDetailError && (
              <div style={{ fontSize: 13, color: "#d13438" }}>❌ {adDetailError}</div>
            )}

            {adDetail && (
              <>
                <Field label="Anzeigename" value={adDetail.displayName} />
                <Field label="UPN" value={adDetail.userPrincipalName} />
                <Field label="Büro" value={adDetail.officeLocation} />
                <Field label="Adresse" value={
                  [adDetail.streetAddress, [adDetail.postalCode, adDetail.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || null
                } />
                <Field label="Bundesland / Land" value={[adDetail.state, adDetail.country].filter(Boolean).join(" / ") || null} />
                <Field label="Telefon (geschäftlich)" value={(adDetail.businessPhones || []).join(", ")} />
                <Field label="Mobiltelefon" value={adDetail.mobilePhone} />
                <Field label="Faxnummer" value={adDetail.faxNumber} />

                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: "#999", marginBottom: 6 }}>Lizenzen</div>
                  {adDetail.licenses?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {adDetail.licenses.map((l) => (
                        <div key={l.skuId} style={{ fontSize: 13 }}>
                          📄 {l.friendlyName}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: "#999" }}>Keine Lizenzen zugewiesen</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

      </div>

      {/* Rohdaten aus HRLab – hilfreich zur Diagnose fehlender Felder */}
      <div className="card" style={{ marginTop: 20 }}>
        <div
          onClick={() => setShowRaw((s) => !s)}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>HRLab-Rohdaten</h2>
          <span style={{ fontSize: 13, color: "#0078d4" }}>{showRaw ? "Ausblenden ▲" : "Anzeigen ▼"}</span>
        </div>
        {showRaw && (
          <pre style={{
            marginTop: 14, fontSize: 11.5, background: "#111", color: "#0f0",
            padding: 14, borderRadius: 8, overflow: "auto", maxHeight: 500,
            whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.5,
          }}>
            {JSON.stringify(emp.raw_data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
